import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tag } from "antd";
import { LoadingOutlined, RightOutlined } from "@ant-design/icons";
import { listPodcasts } from "@vojomo-core/shared";
import type { Podcast } from "@vojomo-core/shared";

interface ArtifactTile {
  key: string;
  icon: string;     // emoji icon
  title: string;
  description: string;
  available: boolean;
  path?: string;
  accent: string;   // background tint color
}

const TILES: ArtifactTile[] = [
  {
    key: "documents",
    icon: "📄",
    title: "Text Documents",
    description: "AI-generated reports, briefings, and topic documents",
    available: true,
    path: "/documents/docs",
    accent: "#eef2ff",
  },
  {
    key: "audio",
    icon: "🎙",
    title: "Audio",
    description: "Podcast episodes generated from your team's data",
    available: true,
    path: "/studio/audio/home",
    accent: "#f0fdf4",
  },
  {
    key: "video",
    icon: "🎬",
    title: "Video",
    description: "Auto-generated video summaries and walkthroughs",
    available: false,
    accent: "#fff7ed",
  },
  {
    key: "slides",
    icon: "🖼",
    title: "Slide Decks",
    description: "AI-assembled presentations from your roadmap and data",
    available: false,
    accent: "#fdf4ff",
  },
  {
    key: "images",
    icon: "🖼️",
    title: "Images",
    description: "AI-generated visuals, diagrams, and graphics",
    available: false,
    accent: "#fffbeb",
  },
  {
    key: "visual",
    icon: "📊",
    title: "Visual / Graphic",
    description: "Infographics, charts, and shareable visual summaries",
    available: false,
    accent: "#f0f9ff",
  },
];

const TYPE_LABELS: Record<string, string> = {
  deep_dive: "Deep Dive",
  summary: "Summary",
  analysis: "Analysis",
  interview: "Interview",
  briefing: "Briefing",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export const StudioHome = () => {
  const navigate = useNavigate();
  const [recentPodcasts, setRecentPodcasts] = useState<Podcast[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  useEffect(() => {
    listPodcasts()
      .then((data) => setRecentPodcasts(data.slice(0, 6)))
      .catch(() => {})
      .finally(() => setLoadingRecent(false));
  }, []);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-gray-900"
          style={{ fontFamily: '"Albert Sans", sans-serif' }}>Studio</h1>
        <p className="text-sm text-gray-400 mt-1">
          Create and share AI-generated content artifacts from your team's data
        </p>
      </div>

      {/* Compact tile grid — 2 columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 10,
          marginBottom: 36,
        }}
      >
        {TILES.map((tile) => (
          <button
            key={tile.key}
            disabled={!tile.available}
            onClick={() => tile.path && navigate(tile.path)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
              borderRadius: 12,
              border: "1px solid",
              borderColor: tile.available ? "transparent" : "#f1f5f9",
              background: tile.available ? tile.accent : "#f8fafc",
              cursor: tile.available ? "pointer" : "not-allowed",
              opacity: tile.available ? 1 : 0.55,
              textAlign: "left",
              transition: "box-shadow 0.15s, border-color 0.15s",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              if (tile.available) {
                (e.currentTarget as HTMLButtonElement).style.boxShadow =
                  "0 2px 12px rgba(0,0,0,0.09)";
                (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
              (e.currentTarget as HTMLButtonElement).style.borderColor = tile.available
                ? "transparent"
                : "#f1f5f9";
            }}
          >
            {/* Icon */}
            <span style={{ fontSize: 22, flexShrink: 0 }}>{tile.icon}</span>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: tile.available ? "#1e293b" : "#94a3b8",
                  marginBottom: 1,
                }}
              >
                {tile.title}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.4 }}>
                {tile.available ? tile.description : "Coming soon"}
              </div>
            </div>

            {/* Arrow or Coming Soon */}
            <div style={{ flexShrink: 0 }}>
              {tile.available ? (
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: "rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RightOutlined style={{ fontSize: 10, color: "#64748b" }} />
                </div>
              ) : (
                <Tag
                  style={{
                    fontSize: 10,
                    margin: 0,
                    border: "none",
                    background: "#f1f5f9",
                    color: "#94a3b8",
                    borderRadius: 999,
                    padding: "1px 7px",
                  }}
                >
                  Soon
                </Tag>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Recent Creations */}
      {(loadingRecent || recentPodcasts.length > 0) && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Recent Creations
            </p>
            {recentPodcasts.length > 0 && (
              <button
                onClick={() => navigate("/studio/audio/podcasts")}
                className="text-xs text-blue-500 hover:text-blue-700 transition-colors flex items-center gap-1"
              >
                View all <RightOutlined style={{ fontSize: 10 }} />
              </button>
            )}
          </div>

          {loadingRecent ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {recentPodcasts.map((podcast) => {
                const isGenerating =
                  podcast.status === "generating" || podcast.status === "pending";
                const isFailed = podcast.status === "failed";

                return (
                  <div
                    key={podcast._id}
                    className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-base flex-shrink-0">🎙</span>

                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate block">
                        {podcast.title}
                      </span>
                      <span className="text-xs text-gray-400">
                        {TYPE_LABELS[podcast.podcast_type] || podcast.podcast_type}
                        {" · "}
                        {formatDate(podcast.created_at)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isGenerating && (
                        <span className="text-xs text-blue-500 flex items-center gap-1">
                          <LoadingOutlined spin style={{ fontSize: 11 }} />
                          Generating
                        </span>
                      )}
                      {isFailed && (
                        <span className="text-xs text-red-400">Failed</span>
                      )}
                      {podcast.status === "ready" && (
                        <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
